import System from '../System';
import GameObject from 'GameObject/GameObject';
import Sprite from 'Prototype/Sprite';
import Point from 'Utility/Point';
import { LevelStatus } from 'State/Level/LevelStatus';
import LoadCardData from '../Card/LoadCardData';
import { LevelActions } from 'State/Level/LevelActions';
import Game from 'Game';
import StateManager from 'State/StateManager';
import SDKIntegrationsHelper from 'Utility/SDK/SDKIntegrationsHelper';
import { loadAudio, PlayMusic } from '../Audio/AudioHelper';
import CardsHelper from 'Utility/CardsHelper';
import LevelHelper, {
    getEpisodeIndex,
    getLevelBackgroundResourceData,
    getLevelResourceData,
    scaleSpriteToScreen,
} from 'Utility/LevelHelper';
import { GameActions } from 'State/Game/GameActions';
import { PlayerActions } from 'State/Player/PlayerActions';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { unlockThreeStar } from 'Constants/Level';
import { TGameArea } from 'Utility/Tracking/DataSet/Transaction';
import { resetEventHandlerData, track } from 'Utility/Tracking/Track';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { ILevelDataFile } from './Data/ILevelDataFile';
import { AUDIO_LEVEL_AMBIENT_WORLD, AUDIO_PACKS } from '../Audio';
import { ResourceDriver } from 'Utility/Resource/ResourceManager';
import { CardType } from 'Prototype/Level/Card/CardType';
import { FTUXTrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { WinCriteriaType } from 'Constants/WinCriteria';
import { IWinCriteriaData } from 'State/Level/ILevelState';
import { ContextType } from 'State/Game/ContextType';
import { IGameState } from 'State/Game/IGameState';
import { ICardsState, StreakReward } from 'State/Card/ICardState';
import { getFieldUnlockShare, getThreeStarShare } from 'Constants/AvatarsAndCrops';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { FTUXAutoPlayLevelCap } from 'Constants/FTUX';
import { LoadEvents, LoadEventType, LoadtimeTrackerSingleton } from 'Utility/LoadtimeTracker';
import { addDragonbonesResources } from 'Utility/Resource/ResourceHelper';
import { DRAGONBONES_PATH } from 'Constants/Dragonbones';
import { ProgressStateHelper } from 'State/Player/Progress/ProgressStateHelper';
import { PreBoosterType } from 'State/Card/BoosterType';
import { CardTypeMap } from 'Prototype/Level/Card/CardTypeMap';
import { ModifierTypeMap } from 'Prototype/Level/Card/ModifierTypeMap';
import TransformComponent from 'Component/Transform';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import { LevelHud } from 'System/UI/HUD/LevelHud';
import { plinkoTransitionSceneId } from 'State/Game/GameScene';
import { getPlinkoConfig } from 'System/Plinko/Helpers';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import LevelStateHelper from 'State/Level/LevelStateHelper';
import { IResourceData } from 'Utility/Resource/IResourceData';
import { report } from 'report';

export default class LevelSystem extends System {
    public backgroundSprite: Sprite;
    public levelData: ILevelDataFile;
    public levelNumber: number;
    private worldNumber: number;
    private levelResourceData: IResourceData;
    private levelBgResourceData: IResourceData;
    public levelAudioClip: string;
    private isDebugLevel: boolean = false;

    constructor(state: StateManager, data: { levelNumber: number }) {
        super(state);

        this.validateDebugLevelData(this.store.getState().levelState.debugLevelData);
        
        this.levelNumber = this.levelData ? Number.parseInt(this.levelData.id.replace('level_', '')) : data.levelNumber;
        this.worldNumber = WorldDataHelper.getWorldNumber(this.levelNumber);
        this.levelResourceData = this.levelData ? undefined : getLevelResourceData(this.levelNumber, this.worldNumber);
        console.log('LevelSystem', this.levelNumber, this.levelData);

        track('tGameArea', TGameArea.LEVEL);
        track('tLevelContext', this.levelNumber);
    }

    public async preload() {
        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_levelSystemPreload, LoadEventType.Start);
        console.log('Level System beginning to preload');

        // Load all required card audios from audio sprite sheet.
        // please take a look at README on resources/audio directory.
        Game.resource.add(AUDIO_PACKS.levelSystem, undefined, ResourceDriver.SECONDARY);

        // Load cardEffects Sprite sheet
        Game.resource.add('CardsVFX', 'resource/graphics/sprite_sheets/cards-effect.json');

        // Level data
        if (this.levelResourceData) Game.resource.add(this.levelResourceData.key, this.levelResourceData.path);
        Game.resource.add('bonus_card_layouts', 'resource/data/bonus_card_layouts.json');
        Game.resource.add('atlas-package_2', 'resource/graphics/sprite_sheets/package_2.json');

        // Load the level data.
        await Game.resource.load();

        this.levelData = this.levelData ?? Game.resource.get(this.levelResourceData.key);
        this.levelBgResourceData = getLevelBackgroundResourceData(this.levelData.settings.background, this.worldNumber);

        Game.resource.add(this.levelBgResourceData.key, this.levelBgResourceData.path);

        if (this.levelNumber >= unlockThreeStar && !this.isDebugLevel) {
            const fieldData = WorldDataHelper.getFieldByLevelNumber(this.levelNumber, this.worldNumber);
            const { key, path } = getThreeStarShare(fieldData.id);
            Game.resource.add(key, path);
        }

        let worldNumber = 0;

        this.levelAudioClip = AUDIO_LEVEL_AMBIENT_WORLD[worldNumber];

        loadAudio(this.levelAudioClip);

        const uniqueCardTypes = LevelHelper.getUniqueCardTypes(this.levelData.cards);
        if (uniqueCardTypes.length > 0) {
            this.preloadCards(uniqueCardTypes);
        }

        const uniqueCardModifierTypes = LevelHelper.getUniqueCardModifierTypes(this.levelData.cards);
        if (uniqueCardModifierTypes.length > 0) {
            this.preloadModifiers(uniqueCardModifierTypes);
        }

        // Always load wild card data because it is used as a streak reward as well
        addDragonbonesResources('booster_card_wildcard', `${DRAGONBONES_PATH}/PreBoosters/`);
        addDragonbonesResources('combo_meter_gold_state', `${DRAGONBONES_PATH}/ComboMeterGoldCircle/`);
        addDragonbonesResources('level_end_special', `${DRAGONBONES_PATH}/PreBoosters/`);

        if (this.store.getState().levelState.preBoosters.includes(PreBoosterType.CANISTER)) {
            addDragonbonesResources('booster_card_canister', `${DRAGONBONES_PATH}/PreBoosters/`);
        }

        if (this.store.getState().levelState.preBoosters.includes(PreBoosterType.REMOVE_THREE)) {
            addDragonbonesResources('card_destroy_red', `${DRAGONBONES_PATH}/PreBoosters/`);
            addDragonbonesResources('card_destroy_black', `${DRAGONBONES_PATH}/PreBoosters/`);
        }

        await Game.resource.load();

        if (WorldDataHelper.isHardLevel(this.levelNumber) && !this.isDebugLevel) {
            this.preloadHardLevelShareAssets();
        }

        LoadtimeTrackerSingleton.trackLoadTask(LoadEvents.SOFA_levelSystemPreload, LoadEventType.End);

        console.log('Level System done preloading');
    }

    private preloadHardLevelShareAssets() {
        const fieldData = WorldDataHelper.getFieldByLevelNumber(this.levelNumber, this.worldNumber);
        const { key, path } = getFieldUnlockShare(fieldData.id);

        Game.resource.add(key, path);
        Game.resource.load();
    }

    private async preloadCards(cardTypes: CardType[]): Promise<void> {
        for (let i = 0; i < cardTypes.length; i++) {
            await CardTypeMap[cardTypes[i]].prototype.preload();
        }
    }

    private async preloadModifiers(cardModifierTypes: CardModifierType[]): Promise<void> {
        for (let i = 0; i < cardModifierTypes.length; i++) {
            await ModifierTypeMap[cardModifierTypes[i]].prototype.preload();
        }
    }

    public onStart(): void {
        this.backgroundSprite = GameObject.Instantiate<Sprite>(Sprite, {
            name: 'backgroundSprite',
            texture: this.levelBgResourceData.key,
            anchor: new Point(0.5, 0.5),
            relativePosition: new Point(0.5, 0.5),
        });
        scaleSpriteToScreen(this.backgroundSprite);
        const bgTransform = this.backgroundSprite.getComponent(TransformComponent);
        bgTransform.scale.set(bgTransform.scale.x * 1.03);
        this.scene.addGameObject(this.backgroundSprite);

        PlayMusic(this.levelAudioClip);
    }

    public handleStateChanged(state: IGameState) {
        const previousState = Game.store.getPreviousState();
        switch (state.levelState.status) {
            case LevelStatus.LOADING:
                try {
                    console.log('Level loading');
                    const stars = [
                        this.levelData.settings.star_1,
                        this.levelData.settings.star_2,
                        this.levelData.settings.star_3,
                    ];
                    const winCriteria: IWinCriteriaData[] = this.levelData.settings.win_criteria
                        ? this.levelData.settings.win_criteria
                        : [{ type: WinCriteriaType.CLEAR_ALL }];

                    let stackCards: number[] = [];

                    // Needed for levels that don't have preset decks
                    if (!Array.isArray(this.levelData.settings.cards_in_stack)) {
                        const cardsInStack: number =
                            this.levelData.settings.cards_in_stack === '0'
                                ? 1
                                : Number(this.levelData.settings.cards_in_stack);
                        stackCards = [
                            ...new Array(
                                // Create Array of length equal to the amount of cards in stack
                                cardsInStack
                            ),
                            //Map values to -1 for random assortments except first value which should be set to the initial card value
                        ].map((card, index) => (index === 0 ? this.levelData.settings.initial_card_value || -1 : -1));
                    } else {
                        stackCards = this.levelData.settings.cards_in_stack.slice();
                    }

                    const cardData = LoadCardData(
                        {
                            cardsInPlay: this.levelData.cards,
                            cardsInStack: stackCards,
                        },
                        new CardsHelper()
                    );
                    const cardsState: ICardsState = {
                        cards: {
                            ...cardData.cardsInPlay,
                            ...cardData.cardsInStack,
                        },
                        inPlay: Object.values(cardData.cardsInPlay).map(c => c.id),
                        inStack: Object.values(cardData.cardsInStack).map(c => c.id),
                        folded: [],
                        discarded: [],
                        turns: 0,
                        tasks: [],
                        turnScore: 0,
                        undoHistory: [],
                        matchSequence: [],
                        streakEnabled: false,
                        streakReward: StreakReward.OneCard,
                        streakScore: 0,
                    };

                    LevelActions.setLevelLoaded({
                        cardsState: cardsState,
                        stars: stars,
                        firstAttempt: LevelStateHelper.isFirstLevelAttempt(
                            state,
                            state.levelState.levelNumber,
                            state.levelState.levelType
                        ),
                        winCriteria,
                    });
                } catch (error) {
                    report('Failed to load level', {
                        once: true,
                        sampleRate: 0.1,
                        error,
                        fingerprint: 'LevelSystem:handleStateChanged:FailedToLoadLevel',
                        tags: {
                            levelNumber: state.levelState.levelNumber,
                        },
                    });
                }
                break;
            case LevelStatus.LOADED:
                console.log('Level loaded');
                //@ts-ignore
                window.GBCXPromo.gameStart();
                LevelActions.levelIntro();
                SDKIntegrationsHelper.loadLevelLeaderboard(state.levelState.levelNumber);
                this.trackLoaded();
                break;
            case LevelStatus.INTRO:
                break;
            case LevelStatus.COMPLETE:
                if (previousState.levelState.status !== LevelStatus.COMPLETE) {
                    console.log('Level complete');

                    // Update the coin bar before the player is rewarded
                    LevelHud.instance.coinBar.handleStateChanged(state);

                    PlayerActions.completeLevel();
                }
                break;
            case LevelStatus.ACTIVE:
                break;
            case LevelStatus.EXITING:
                if (state.context.current === ContextType.DEFAULT) {
                    // If player has a plinko token start the minigame else, normal flow
                    if (InventoryStateHelper.hasInInventory(state, RewardType.PLINKO_TOKEN)) {
                        const config = getPlinkoConfig();
                        GameActions.loadPlinkoScene({
                            loadingSceneId: plinkoTransitionSceneId,
                            purchasableTokensRemaining: config.purchasesAllowed,
                        });
                    } else {
                        const highestLevelCompleted = getHighestLevelCompleted(state.playerState);

                        if (highestLevelCompleted < FTUXAutoPlayLevelCap && state.playerState.sessionCount === 1) {
                            GameActions.loadLevel({ levelNumber: highestLevelCompleted + 1, preBoosters: [] });
                        } else {
                            GameActions.loadWorld({
                                firstComplete: state.levelState.firstComplete ?? true,
                                starsGained:
                                    (state.levelState.completeData && state.levelState.completeData.starsGained) || 0,
                            });
                        }
                    }
                }
                //@ts-ignore
                window.GBCXPromo.gameOver();
                break;
            case LevelStatus.RESTARTING:
                if (state.context.current === ContextType.DEFAULT) {
                    GameActions.loadLevel({
                        levelNumber: this.levelNumber,
                        preBoosters: state.levelState.preBoosters,
                        restart: true,
                    });
                }
                break;
        }
    }

    private trackLoaded() {
        const state: IGameState = Game.store.getState();
        switch (this.levelNumber) {
            case 2:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_08_Level2Loaded)) {
                    Game.sdk.gameTracking(FBTrackingEvent.FTUE_08_Level2Loaded);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_08_Level2Loaded);

                    track('stepId', 8);
                    track('stepName', FBTrackingEvent.FTUE_08_Level2Loaded);
                    resetEventHandlerData(FTUXTrackingEventName.ftux, Game.store.getState());
                }
                break;
            case 3:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_11_Level3Loaded)) {
                    Game.sdk.gameTracking(FBTrackingEvent.FTUE_11_Level3Loaded);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.FTUE_11_Level3Loaded);

                    track('stepId', 11);
                    track('stepName', FBTrackingEvent.FTUE_11_Level3Loaded);
                    resetEventHandlerData(FTUXTrackingEventName.ftux, Game.store.getState());
                }
                break;
            case 4:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.Gamefield4IsShow)) {
                    Game.sdk.gameTracking(FBTrackingEvent.Gamefield4IsShow);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.Gamefield4IsShow);
                }
                break;
            case 5:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.Gamefield5IsShow)) {
                    Game.sdk.gameTracking(FBTrackingEvent.Gamefield5IsShow);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.Gamefield5IsShow);
                }
                break;
            case 6:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.Gamefield6IsShow)) {
                    Game.sdk.gameTracking(FBTrackingEvent.Gamefield6IsShow);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.Gamefield6IsShow);
                }
                break;
            case 7:
                if (!state.playerState.FBEvents.includes(FBTrackingEvent.Gamefield7IsShow)) {
                    Game.sdk.gameTracking(FBTrackingEvent.Gamefield7IsShow);
                    PlayerActions.addFTUETrackingEvent(FBTrackingEvent.Gamefield7IsShow);
                }
                break;
        }
    }

    private validateDebugLevelData(levelData: ILevelDataFile) {
        if (!levelData) return;

        const isNewLevel = Number.parseInt(levelData.id.replace('level_', '')) > WorldDataHelper.maxLevel();
        this.levelData = {
            ...levelData,
            id: isNewLevel ? 'level_10' : levelData.id,
            settings: {
                ...levelData.settings,
                background: isNewLevel ? 'episode_1.jpg' : levelData.settings.background,
            },
        };

        this.isDebugLevel = true;
    }

    // Dispatch first "play" folding the first card.
    //this.cardSystem.foldNextCard();

    public init(): void { }
    protected onDestroy(): void { }
    protected onLateUpdate(): void { }
    protected onStop(): void { }
    protected onUpdate(): void { }
}
